import { REASONS_FOR_VISIT_ERRORS } from "@src/constants";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { fetchReasonsForVisit, type ReasonForVisit } from "@src/services/reasons-for-visit.service";
import { useEffect, useState } from "react";

/**
 * Fetches the reasons-for-visit list from the Self Service API when
 * FEATURE_REASONS_FOR_VISIT_API is enabled. Callers fall back to the
 * static FilteredSpecialistOptions/LegacyFilteredSpecialistOptions lists
 * when the flag is off, or show `error` inline when the fetch fails.
 */
export const useReasonsForVisit = () => {
  const { FEATURE_REASONS_FOR_VISIT_API } = useFeatureFlags();
  const [reasonsForVisit, setReasonsForVisit] = useState<ReasonForVisit[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(FEATURE_REASONS_FOR_VISIT_API);

  useEffect(() => {
    if (!FEATURE_REASONS_FOR_VISIT_API) {
      setReasonsForVisit([]);
      setError("");
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadReasonsForVisit = async () => {
      const result = await fetchReasonsForVisit();
      if (cancelled) return;

      if ("error" in result) {
        setReasonsForVisit([]);
        setError(REASONS_FOR_VISIT_ERRORS.FETCH_FAILED);
        setIsLoading(false);
        return;
      }

      setReasonsForVisit(result);
      setError("");
      setIsLoading(false);
    };

    loadReasonsForVisit();

    return () => {
      cancelled = true;
    };
  }, [FEATURE_REASONS_FOR_VISIT_API]);

  return { reasonsForVisit, error, isLoading };
};
